import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { BasemapReleasePanel } from "../components/BasemapReleasePanel";
import { FileCode2, ShieldCheck, Plus, Trash2, Save, X, Search, FileDigit } from "lucide-react";

const EMPTY = { motor_name: "", part_code: "", ecm_id: "", binFile: null, xdfFile: null };

export default function EcuLibrary() {
  const { isAdmin, loading, login } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setRows((await api.get("/library")).data || []); } catch { setRows([]); }
  }, []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (!loading && !isAdmin) {
    return (
      <div className="p-8 max-w-md mx-auto text-center mt-16">
        <div className="h-14 w-14 rounded-xl bg-red-500/15 border border-red-700/40 flex items-center justify-center mx-auto mb-4">
          <ShieldCheck className="h-7 w-7 text-cyan-300" />
        </div>
        <h1 className="font-head font-black text-3xl uppercase text-white">Admin Only</h1>
        <p className="text-slate-500 text-sm mt-2">Masuk dengan akun admin untuk mengelola ECU Library.</p>
        <div className="flex gap-3 justify-center mt-5">
          <Button data-testid="ecolib-login-btn" onClick={login} className="bg-gradient-to-r from-cyan-400 to-teal-500 text-slate-950 font-bold">Admin Login</Button>
          <Button variant="ghost" onClick={() => navigate("/map-editor")} className="text-slate-400 hover:text-white hover:bg-slate-800">Back</Button>
        </div>
      </div>
    );
  }

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const resetForm = () => { setForm(EMPTY); setEditId(null); };

  const save = async () => {
    if (!form.part_code.trim()) { toast.error("Part Code wajib diisi"); return; }
    setSaving(true);
    try {
      const body = { motor_name: (form.motor_name || "").trim(), part_code: form.part_code.trim(), ecm_id: (form.ecm_id || "").trim() };
      const res = editId ? await api.put(`/library/${editId}`, body) : await api.post("/library", body);
      const id = res.data.id;
      for (const kind of ["bin", "xdf"]) {
        const f = form[`${kind}File`];
        if (f) { const fd = new FormData(); fd.append("file", f); await api.post(`/library/${id}/upload?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      }
      toast.success(editId ? "Entri diperbarui" : "Entri ditambahkan");
      resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const edit = (r) => {
    setForm({ motor_name: r.motor_name || "", part_code: r.part_code || "", ecm_id: r.ecm_id || "", binFile: null, xdfFile: null });
    setEditId(r.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Hapus entri ini beserta file BIN/XDF-nya?")) return;
    try { await api.delete(`/library/${id}`); toast.success("Dihapus"); if (editId === id) resetForm(); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  const filtered = rows.filter((r) => {
    const s = (q || "").toLowerCase();
    return !s || [r.motor_name, r.part_code, r.ecm_id, r.bin_name, r.xdf_name].some((v) => (v || "").toLowerCase().includes(s));
  });

  const inputCls = "bg-[#121824] border-slate-700 text-slate-100 focus-visible:ring-cyan-400 font-mono-tt text-sm";

  return (
    <div className="p-4 sm:p-8 max-w-[1200px] mx-auto" data-testid="ecu-library-page">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><FileCode2 className="h-4 w-4" /> Admin Console</div>
        <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Update Basemap</h1>
        <p className="text-slate-500 text-sm mt-1">Upload BIN + XDF per Part Code. User (yang sudah di-approve) cukup ketik Part Code di Map Editor — BIN terbuka & XDF otomatis ter-load.</p>
      </div>

      <BasemapReleasePanel refreshKey={rows.length} title="Release Basemap" button="Release Basemap" />

      {/* Form */}
      <div data-testid="ecolib-form" className="rounded-xl border border-slate-800 bg-[#0c1017] p-5 mb-8">
        <h2 className="font-head font-bold text-xl uppercase text-white flex items-center gap-2 mb-4">
          <FileCode2 className="h-5 w-5 text-cyan-400" /> {editId ? "Edit Entri" : "Tambah Entri"}
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-slate-400">Nama Motor</Label>
            <Input data-testid="ecolib-motor-name" value={form.motor_name} onChange={(e) => setF("motor_name", e.target.value)} placeholder="PCX 160" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400">Part Code</Label>
            <Input data-testid="ecolib-part-code" value={form.part_code} onChange={(e) => setF("part_code", e.target.value)} placeholder="38770-K2K-B01" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400">ECM ID</Label>
            <Input data-testid="ecolib-ecm-id" value={form.ecm_id} onChange={(e) => setF("ecm_id", e.target.value)} placeholder="0103790F03" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><FileDigit className="h-3.5 w-3.5 text-emerald-400" /> File BIN {editId && <span className="text-slate-600 normal-case tracking-normal">(kosongkan jika tak ganti)</span>}</Label>
            <input data-testid="ecolib-bin-file" type="file" accept=".bin" onChange={(e) => setF("binFile", e.target.files[0])} className="block w-full text-xs text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-700 file:text-slate-200" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><FileCode2 className="h-3.5 w-3.5 text-sky-400" /> File XDF {editId && <span className="text-slate-600 normal-case tracking-normal">(kosongkan jika tak ganti)</span>}</Label>
            <input data-testid="ecolib-xdf-file" type="file" accept=".xdf" onChange={(e) => setF("xdfFile", e.target.files[0])} className="block w-full text-xs text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-700 file:text-slate-200" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <Button data-testid="ecolib-save-btn" onClick={save} disabled={saving} className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-slate-950 font-bold disabled:opacity-50">
            {editId ? <><Save className="h-4 w-4 mr-1.5" /> {saving ? "Menyimpan…" : "Update"}</> : <><Plus className="h-4 w-4 mr-1.5" /> {saving ? "Menyimpan…" : "Tambah"}</>}
          </Button>
          {editId && (
            <Button data-testid="ecolib-cancel-btn" onClick={resetForm} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
              <X className="h-4 w-4 mr-1.5" /> Batal
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h2 className="font-head font-bold text-xl uppercase text-white flex items-center gap-2"><FileCode2 className="h-5 w-5 text-cyan-400" /> Entri <span className="text-slate-600 text-sm font-mono-tt">({rows.length})</span></h2>
        <div className="relative">
          <Search className="h-4 w-4 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input data-testid="ecolib-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari motor / part / ECM / file" className="bg-[#121824] border-slate-700 text-slate-100 pl-8 h-9 w-[240px] text-sm" />
        </div>
      </div>
      <div className="rounded-xl border border-slate-800 overflow-hidden bg-[#0c1017]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ecolib-table">
            <thead>
              <tr className="bg-[#121824] border-b border-slate-800">
                {["Nama Motor", "Part Code", "ECM ID", "BIN", "XDF", "Actions"].map((h) => (
                  <th key={h} className="text-left px-3 py-3 font-mono-tt text-[11px] uppercase tracking-wider text-slate-400 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`ecolib-row-${r.id}`} className="border-b border-slate-800/60 hover:bg-cyan-400/5">
                  <td data-testid={`ecolib-motor-${r.id}`} className="px-3 py-2.5 text-slate-200 whitespace-nowrap">{r.motor_name || "-"}</td>
                  <td className="px-3 py-2.5 text-slate-200 whitespace-nowrap font-semibold">{r.part_code}</td>
                  <td className="px-3 py-2.5 font-mono-tt text-emerald-400 whitespace-nowrap">{r.ecm_id || "-"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{r.has_bin ? <span className="text-slate-300 text-xs font-mono-tt" title={r.bin_name}>✓ {r.bin_name || "BIN"}</span> : <span className="text-slate-600 text-xs">—</span>}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{r.has_xdf ? <span className="text-slate-300 text-xs font-mono-tt" title={r.xdf_name}>✓ {r.xdf_name || "XDF"}</span> : <span className="text-slate-600 text-xs">—</span>}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="flex gap-1.5">
                      <button data-testid={`ecolib-edit-${r.id}`} onClick={() => edit(r)} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 text-xs"><Save className="h-3.5 w-3.5" /> Edit</button>
                      <button data-testid={`ecolib-delete-${r.id}`} onClick={() => del(r.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-800 text-red-400 hover:bg-red-950/40 text-xs"><Trash2 className="h-3.5 w-3.5" /> Hapus</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-600 font-mono-tt">Belum ada entri. Tambahkan di atas.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
